/**
 * ConfidenceSheet — 신뢰도 4등급과 '추정' 표기의 뜻을 설명하는 시트.
 *
 * 신뢰도 배지는 이 앱의 **정직성 장치**다 (PRD §5.6). 그런데 화면 어디에도
 * "보통이 무슨 뜻인지" 가 없으면 배지는 장식으로 읽힌다 — 사용자는 '보통'을
 * 보고도 이 값을 믿어야 할지 판단할 수 없다.
 *
 * 등급 이름만 나열하지 않고 **무엇을 근거로 그 등급이 붙었는지**를 같이 적는다.
 * 그래야 옆에 보이는 '실거래 6건 · 산포 넓음' 이 배지의 근거로 연결된다.
 */
import { useTranslation } from "react-i18next";
import { Badge } from "@astryxdesign/core/Badge";
import { Divider } from "@astryxdesign/core/Divider";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { VStack, HStack } from "@astryxdesign/core/Layout";
import Sheet from "../feedback/Sheet";

/*
 * 4등급과 배지 색은 **여기서 직접 든다** — 종전엔 `data/priceModel` 에서 가져왔는데,
 * 그 모듈이 `data/dummyCards` 를 끌고 오고 dummyCards 는 import 만으로 i18n 을
 * 초기화한다(CLAUDE.md). 이 시트는 실 카드 상세에서 열리므로 그 체인이 프로덕션
 * 번들과 raw-key 단언 테스트에 그대로 딸려 들어간다. 값은 네 줄뿐이라 옮겨 든다.
 */
type PriceConfidence = "high" | "medium" | "low" | "none";

const CONFIDENCE_BADGE: Record<PriceConfidence, "success" | "info" | "warning" | "neutral"> = {
  high: "success",
  medium: "info",
  low: "warning",
  none: "neutral",
};

const LEVELS: PriceConfidence[] = ["high", "medium", "low", "none"];

interface ConfidenceSheetProps {
  open: boolean;
  onClose: () => void;
}

export default function ConfidenceSheet({ open, onClose }: ConfidenceSheetProps) {
  const { t } = useTranslation("card");

  return (
    <Sheet open={open} onClose={onClose} ariaLabel={t("confidence.title")} backKind="confidence-sheet">
      <VStack gap={4} padding={5}>
        <VStack gap={1}>
          <Heading level={3}>{t("confidence.title")}</Heading>
          <Text type="supporting" color="secondary">{t("confidence.intro")}</Text>
        </VStack>

        <VStack gap={4}>
          {LEVELS.map((level) => (
            <VStack key={level} gap={1} align="start">
              <Badge
                variant={CONFIDENCE_BADGE[level]}
                label={t(`price.confidence.${level}`)}
              />
              <Text type="supporting" color="secondary">
                {t(`confidence.level.${level}`)}
              </Text>
            </VStack>
          ))}
        </VStack>

        <Divider />

        {/* '추정' 은 신뢰도와 다른 축이다 — 등급별 표에서 실거래가 아예 없는 행에 붙는다. */}
        <VStack gap={1} align="start">
          <HStack gap={2} align="center">
            <Badge variant="neutral" label={t("price.estimated")} />
          </HStack>
          <Text type="supporting" color="secondary">{t("confidence.estimated")}</Text>
        </VStack>
      </VStack>
    </Sheet>
  );
}
